/**
 * Gerenciador de macros (`/macros`): as da mesa (mestres editam, todos usam), as
 * minhas desta mesa e as minhas globais. Ver ./model.ts.
 */
import { useEffect, useState } from 'react';
import { openDoc, releaseDoc } from '../../plugins/ndbClient.js';
import { useT } from '../../i18n/index.js';
import type { Op, ReplicaNodeDatabase } from '../../ndb.js';
import { useApp } from '../../store.js';
import { Button, Dialog } from '../../ui/controls.js';
import { Icon } from '../../ui/icons.js';
import { askConfirm } from '../../ui/prompt.js';
import { toast } from '../../ui/Toaster.js';
import { MACRO_NAME, addRoomMacroOps, readRoomMacros, saveMyMacros, updateRoomMacroOps, useMyMacros, type Macro, type MyMacros } from './model.js';

/** As macros da mesa (documento do item `macros`), acompanhando as mudanças. */
export function useRoomMacros(): { macros: Macro[]; db: ReplicaNodeDatabase | null } {
  const itemId = useApp((s) => s.library.find((i) => i.kind === 'macros')?.id ?? null);
  const [db, setDb] = useState<ReplicaNodeDatabase | null>(null);
  const [, setVersion] = useState(0);
  useEffect(() => {
    if (!itemId) {
      setDb(null);
      return;
    }
    let alive = true;
    let off: (() => void) | undefined;
    void openDoc(itemId).then((opened) => {
      if (!alive) return;
      setDb(opened);
      off = opened.subscribe(() => setVersion((v) => v + 1));
    });
    return () => {
      alive = false;
      off?.();
      releaseDoc(itemId);
    };
  }, [itemId]);
  return { macros: db ? readRoomMacros(db.visible()) : [], db };
}

type Tab = 'room' | 'mine' | 'global';

export function MacrosDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const me = useApp((s) => s.user);
  const room = useApp((s) => s.room);
  const isGm = useApp((s) => !!s.user && !!s.room && (s.room.ownerId === s.user.id || !!s.members[s.user.id]?.modes.includes('gm')));
  const hasItem = useApp((s) => s.library.some((i) => i.kind === 'macros'));
  const createItem = useApp((s) => s.createLibraryItem);
  const { macros: roomMacros, db } = useRoomMacros();
  const mine = useMyMacros(me?.id);
  const [tab, setTab] = useState<Tab>('room');

  // o mestre abre o gerenciador numa mesa sem macros: cria o documento delas
  useEffect(() => {
    if (isGm && !hasItem) createItem({ kind: 'macros', name: 'Macros', parentId: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGm, hasItem]);

  const writeRoom = (ops: Op[]) => {
    if (!db) return;
    try {
      db.write(ops);
    } catch (error) {
      toast(t('macro.saveFailed', { message: error instanceof Error ? error.message : String(error) }), 'error');
    }
  };
  const updateMine = (fn: (m: MyMacros) => MyMacros) => me && saveMyMacros(me.id, fn(mine));
  const roomId = room?.id ?? '';
  const mineHere = mine.rooms[roomId] ?? [];

  const list: Macro[] = tab === 'room' ? roomMacros : tab === 'mine' ? mineHere : mine.global;
  const editable = tab !== 'room' || isGm;

  const save = (next: Macro) => {
    if (tab === 'room') writeRoom(updateRoomMacroOps(next));
    else if (tab === 'mine') updateMine((m) => ({ ...m, rooms: { ...m.rooms, [roomId]: mineHere.map((x) => (x.id === next.id ? next : x)) } }));
    else updateMine((m) => ({ ...m, global: m.global.map((x) => (x.id === next.id ? next : x)) }));
  };
  const add = () => {
    const base = t('macro.newName');
    let name = base;
    for (let i = 2; list.some((m) => m.name.toLowerCase() === name.toLowerCase()); i++) name = `${base}${i}`;
    const macro: Macro = { id: `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, name, lines: '' };
    if (tab === 'room') {
      if (db) writeRoom(addRoomMacroOps(db.visible().root.id, db.newId(), macro));
    } else if (tab === 'mine') updateMine((m) => ({ ...m, rooms: { ...m.rooms, [roomId]: [...mineHere, macro] } }));
    else updateMine((m) => ({ ...m, global: [...m.global, macro] }));
  };
  const remove = async (macro: Macro) => {
    const sure = await askConfirm({ title: t('macro.delete'), message: t('macro.delete.confirm', { name: macro.name }), action: t('macro.delete'), danger: true });
    if (!sure) return;
    if (tab === 'room') writeRoom([{ t: 'del', node: macro.id }]);
    else if (tab === 'mine') updateMine((m) => ({ ...m, rooms: { ...m.rooms, [roomId]: mineHere.filter((x) => x.id !== macro.id) } }));
    else updateMine((m) => ({ ...m, global: m.global.filter((x) => x.id !== macro.id) }));
  };

  return (
    <Dialog
      title={t('macro.manager')}
      wide
      onClose={onClose}
      actions={
        <>
          {editable ? (
            <Button variant="secondary" onClick={add} disabled={tab === 'room' && !db} icon="plus">
              {t('macro.new')}
            </Button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="action" onClick={onClose}>
            {t('common.close')}
          </Button>
        </>
      }
    >
      <div className="rd-macros">
        <div className="rd-macros__tabs" role="tablist">
          {(['room', 'mine', 'global'] as Tab[]).map((id) => (
            <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'is-on' : ''} onClick={() => setTab(id)}>
              {t(`macro.tab.${id}`)}
            </button>
          ))}
        </div>
        <p className="rd-macros__hint">{t(`macro.hint.${tab}`)}</p>
        {list.length === 0 ? (
          <p className="rd-macros__empty">{t('macro.empty')}</p>
        ) : (
          <ul className="rd-macros__list">
            {list.map((macro) => (
              <MacroRow key={macro.id} macro={macro} editable={editable} onSave={save} onDelete={() => void remove(macro)} taken={(name) => list.some((m) => m.id !== macro.id && m.name.toLowerCase() === name.toLowerCase())} />
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

function MacroRow({ macro, editable, onSave, onDelete, taken }: { macro: Macro; editable: boolean; onSave: (m: Macro) => void; onDelete: () => void; taken: (name: string) => boolean }) {
  const t = useT();
  const [name, setName] = useState(macro.name);
  const [lines, setLines] = useState(macro.lines);
  useEffect(() => setName(macro.name), [macro.name]);
  useEffect(() => setLines(macro.lines), [macro.lines]);
  const nameError = !MACRO_NAME.test(name) ? t('macro.name.invalid') : taken(name) ? t('macro.name.taken') : null;
  const commit = () => {
    if (nameError) return;
    if (name !== macro.name || lines !== macro.lines) onSave({ ...macro, name, lines });
  };
  if (!editable) {
    return (
      <li className="rd-macros__item">
        <code>/{macro.name}</code>
        <pre>{macro.lines}</pre>
      </li>
    );
  }
  return (
    <li className="rd-macros__item">
      <div className="rd-macros__head">
        <span>/</span>
        <input aria-label={t('macro.name')} value={name} maxLength={30} onChange={(e) => setName(e.target.value.replace(/\s/g, ''))} onBlur={commit} aria-invalid={!!nameError} />
        <button type="button" className="rd-macros__del" aria-label={t('macro.delete')} title={t('macro.delete')} onClick={onDelete}>
          <Icon name="trash" size={14} />
        </button>
      </div>
      {nameError ? <span className="rd-macros__error">{nameError}</span> : null}
      <textarea aria-label={t('macro.lines', { name })} value={lines} rows={3} placeholder={t('macro.lines.placeholder')} onChange={(e) => setLines(e.target.value)} onBlur={commit} />
    </li>
  );
}
